import { get, post, del } from '../core/http';
import { Product } from './products';

const BASE_PATH = '/v1/storefront/favorites';

// ============================================================================
// Types
// ============================================================================

export interface Favorite {
  id: string;
  userId: string;
  productId: string;
  createdAt: string;
  product?: Product;
}

export interface AddFavoriteRequest {
  productId: string;
}

export interface FavoritesResponse {
  data: Favorite[];
  total: number;
}

export interface PaginatedFavoritesResponse {
  data: Favorite[];
  meta: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

export interface CheckFavoriteResponse {
  isFavorite: boolean;
}

export interface FavoritesCountResponse {
  count: number;
}

// ============================================================================
// API Client
// ============================================================================

export const favoritesApi = {
  /**
   * Add a product to favorites
   * @param productId - The ID of the product to add
   * @returns The created favorite entry
   * @throws {Error} If user is not authenticated or product doesn't exist
   */
  addFavorite: (productId: string): Promise<Favorite> => {
    return post<Favorite>(BASE_PATH, { productId });
  },

  /**
   * Remove a product from favorites
   * @param productId - The ID of the product to remove
   * @returns Success message
   * @throws {Error} If user is not authenticated
   */
  removeFavorite: async (productId: string): Promise<void> => {
    await del(`${BASE_PATH}/${productId}`);
  },

  /**
   * Get all favorites for the authenticated user
   * @returns List of favorites with product details
   * @throws {Error} If user is not authenticated
   */
  getFavorites: (): Promise<FavoritesResponse> => {
    return get<FavoritesResponse>(BASE_PATH);
  },

  /**
   * Get paginated favorites for the authenticated user
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 20)
   * @returns Paginated list of favorites with product details
   * @throws {Error} If user is not authenticated
   */
  getFavoritesPaginated: (
    page: number = 1,
    limit: number = 20
  ): Promise<PaginatedFavoritesResponse> => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    return get<PaginatedFavoritesResponse>(`${BASE_PATH}?${params.toString()}`);
  },

  /**
   * Check if a product is in the user's favorites
   * @param productId - The ID of the product to check
   * @returns Object with isFavorite boolean
   * @throws {Error} If user is not authenticated
   */
  checkFavorite: (productId: string): Promise<CheckFavoriteResponse> => {
    return get<CheckFavoriteResponse>(`${BASE_PATH}/check/${productId}`);
  },

  /**
   * Get the total count of favorites for the authenticated user
   * @returns Object with count number
   * @throws {Error} If user is not authenticated
   */
  getFavoritesCount: (): Promise<FavoritesCountResponse> => {
    return get<FavoritesCountResponse>(`${BASE_PATH}/count`);
  },
};
